/**
 * The link editor: link a CCMS message (TemplateID) to an approved WhatsApp template and map each of the template's
 * variables ({{1}}, URL button parts) to one of the message's fields, with a live phone preview filled with sample
 * values. Opened from the WhatsApp templates page (any module) and from Forms settings, Messages (scope "forms").
 */

import { useEffect, useMemo, useState } from 'react';
import { IconLink, IconLinkOff, IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { Modal, Stack, Group, Text, Select, TextInput, Button, Alert, Badge, SimpleGrid, Paper, Divider } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import type {
  WhatsAppMessageDescriptor,
  WhatsAppTemplateLibraryItem,
  WhatsAppTemplateParameterMapping,
  WhatsAppTemplateVariableView,
} from '@actschurch/ccms-sdk';
import { WhatsAppPhonePreview } from './WhatsAppPhonePreview';
import { previewLinked, statusColor, variablesOf, LINK_STATE_UI } from './whatsappTemplateRules';
import {
  useWhatsAppTemplateLibrary,
  useSaveWhatsAppTemplateLink,
  useDeleteWhatsAppTemplateLink,
  whatsAppErrorText,
  type WhatsAppTemplateScope,
} from './useWhatsAppTemplates';

const key = (v: { component: string; index: number; name?: string | null; buttonIndex?: number | null }) =>
  v.component === 'button' ? `button:${v.buttonIndex ?? 0}` : `${v.component}:${v.name ?? v.index}`;

/** A first guess at the mapping: the existing link, else the message's suggested wording when the texts match, else by order. */
function guessMapping(template: WhatsAppTemplateLibraryItem, message: WhatsAppMessageDescriptor): Record<string, WhatsAppTemplateParameterMapping> {
  const out: Record<string, WhatsAppTemplateParameterMapping> = {};
  const existing = message.link && message.link.metaTemplateName === template.name ? message.link.parameters : null;
  const starter = message.starter;
  const linkField = message.fields.find((f) => f.isLink)?.key ?? '';
  for (const v of template.variables) {
    const k = key(v);
    const found = existing?.find((p) => key(p) === k);
    if (found) {
      out[k] = { ...found };
      continue;
    }
    let field = '';
    if (v.component === 'button') field = starter?.buttons.find((b) => b.field)?.field ?? linkField;
    else if (v.component === 'body' && starter && starter.bodyText.trim() === (template.bodyText ?? '').trim())
      field = starter.variables.find((x) => x.component === 'body' && x.index === v.index)?.field ?? '';
    out[k] = { component: v.component, index: v.index, name: v.name ?? null, buttonIndex: v.buttonIndex ?? null, field, defaultValue: field === 'recipientName' ? 'there' : null };
  }
  return out;
}

export interface TemplateLinkEditorProps {
  opened: boolean;
  onClose: () => void;
  /** The message to link (required unless messages is given). */
  message: WhatsAppMessageDescriptor | null;
  /** Lets the admin pick the message (the templates page's "Link to a message"). */
  messages?: WhatsAppMessageDescriptor[];
  /** Preselect a template (by name and language). */
  initialTemplate?: { name: string; languageCode: string } | null;
  scope?: WhatsAppTemplateScope;
  onSaved?: () => void;
}

export function TemplateLinkEditor({ opened, onClose, message, messages, initialTemplate, scope = 'communications', onSaved }: TemplateLinkEditorProps) {
  const library = useWhatsAppTemplateLibrary(scope, true, opened);
  const save = useSaveWhatsAppTemplateLink(scope);
  const unlink = useDeleteWhatsAppTemplateLink(scope);

  const [messageId, setMessageId] = useState<number | null>(message?.templateId ?? null);
  const [templateKey, setTemplateKey] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, WhatsAppTemplateParameterMapping>>({});
  const [error, setError] = useState<string | null>(null);

  const current = useMemo(
    () => message ?? messages?.find((m) => m.templateId === messageId) ?? null,
    [message, messages, messageId],
  );
  const templates = useMemo(() => (library.data?.templates ?? []).filter((t) => t.status.canSend && !t.isDeleted), [library.data]);
  const template = templates.find((t) => `${t.name}|${t.languageCode}` === templateKey) ?? null;

  // Reset when opened: the message's current link (or the preselected template).
  useEffect(() => {
    if (!opened) return;
    setError(null);
    setMessageId(message?.templateId ?? null);
    const link = message?.link;
    if (initialTemplate) setTemplateKey(`${initialTemplate.name}|${initialTemplate.languageCode}`);
    else if (link) setTemplateKey(`${link.metaTemplateName}|${link.languageCode}`);
    else setTemplateKey(null);
  }, [opened, message, initialTemplate]);

  useEffect(() => {
    if (template && current) setMapping(guessMapping(template, current));
    else setMapping({});
  }, [template?.id, current?.templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = current?.fields ?? [];
  const unmapped = template ? template.variables.filter((v) => !mapping[key(v)]?.field && !mapping[key(v)]?.defaultValue) : [];
  const preview = template ? previewLinked(template, Object.values(mapping), fields) : null;
  const notLinkable = current && (current.isSecurity || !current.whatsAppAllowed);

  const set = (v: WhatsAppTemplateVariableView, patch: Partial<WhatsAppTemplateParameterMapping>) =>
    setMapping((m) => ({ ...m, [key(v)]: { ...(m[key(v)] ?? { component: v.component, index: v.index, name: v.name, buttonIndex: v.buttonIndex, field: '' }), ...patch } }));

  const onSave = async () => {
    if (!current || !template) return;
    setError(null);
    try {
      await save.mutateAsync({
        templateId: current.templateId,
        request: { metaTemplateName: template.name, languageCode: template.languageCode, parameters: Object.values(mapping) },
      });
      toast.success('Linked', `${current.title} now goes by WhatsApp as ${template.name}.`);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(whatsAppErrorText(err, 'The link could not be saved.'));
    }
  };

  const onUnlink = async () => {
    if (!current) return;
    setError(null);
    try {
      await unlink.mutateAsync(current.templateId);
      toast.success('Unlinked', `${current.title} is no longer linked to a template.`);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(whatsAppErrorText(err, 'Unlinking failed.'));
    }
  };

  const fieldOptions = fields.map((f) => ({ value: f.key, label: `${f.label}${f.isLink ? ' (link)' : ''}` }));
  const state = current?.link ? LINK_STATE_UI[current.link.state] ?? LINK_STATE_UI.none : LINK_STATE_UI.none;

  return (
    <Modal opened={opened} onClose={onClose} title="Link a message to a WhatsApp template" size="xl">
      <Stack gap="md">
        {!message && messages && (
          <Select
            label="Message"
            placeholder="Choose the CCMS message"
            searchable
            data={messages
              .filter((m) => !m.isSecurity && m.whatsAppAllowed)
              .map((m) => ({ value: String(m.templateId), label: `${m.moduleName}: ${m.title} (${m.templateId})` }))}
            value={messageId ? String(messageId) : null}
            onChange={(v) => setMessageId(v ? Number(v) : null)}
            data-help-id="communications.whatsapp-templates.link-message"
          />
        )}

        {current && (
          <Paper withBorder radius="md" p="sm">
            <Group justify="space-between" wrap="wrap" gap="xs">
              <div style={{ minWidth: 0 }}>
                <Text fw={600}>{current.title}</Text>
                <Text size="xs" c="dimmed">
                  {current.moduleName} · message {current.templateId}
                  {current.audience ? ` · ${current.audience}` : ''}
                </Text>
              </div>
              <Badge variant="light" color={state.color}>
                {state.label}
              </Badge>
            </Group>
            {current.link?.stateText && (
              <Text size="xs" c="dimmed" mt={6}>
                {current.link.stateText}
              </Text>
            )}
          </Paper>
        )}

        {notLinkable && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            {current?.isSecurity
              ? 'Security messages (codes, passwords and reset links) are never sent by WhatsApp.'
              : `${current?.title} is never sent by WhatsApp (${current?.audience}).`}
          </Alert>
        )}

        {current && !notLinkable && (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Stack gap="sm">
              <Select
                label="Approved template"
                description="Only templates Meta approved can be linked."
                placeholder={library.isLoading ? 'Loading templates' : templates.length === 0 ? 'No approved templates yet' : 'Choose a template'}
                searchable
                data={templates.map((t) => ({ value: `${t.name}|${t.languageCode}`, label: `${t.name} (${t.languageCode}, ${t.category.name})` }))}
                value={templateKey}
                onChange={setTemplateKey}
                data-help-id="communications.whatsapp-templates.link-template"
              />

              {template && template.variables.length === 0 && (
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  This template has no variables: it is sent exactly as approved.
                </Alert>
              )}

              {template && template.variables.length > 0 && (
                <Stack gap="xs" data-help-id="communications.whatsapp-templates.link-fields">
                  <Divider label="Fill each variable from" labelPosition="left" />
                  {template.variables.map((v) => {
                    const m = mapping[key(v)];
                    return (
                      <Paper key={key(v)} withBorder radius="sm" p="xs">
                        <Stack gap={6}>
                          <Group justify="space-between" gap="xs" wrap="nowrap">
                            <Text size="sm" fw={600} ff="monospace">
                              {v.label}
                            </Text>
                            {v.example && (
                              <Text size="xs" c="dimmed" truncate>
                                Meta sample: {v.example}
                              </Text>
                            )}
                          </Group>
                          <Group gap="xs" grow wrap="nowrap" align="flex-start">
                            <Select
                              placeholder="Choose a field"
                              data={v.component === 'button' ? fieldOptions.filter((o) => fields.find((f) => f.key === o.value)?.isLink) : fieldOptions}
                              value={m?.field || null}
                              onChange={(val) => set(v, { field: val ?? '' })}
                              searchable
                              clearable
                              size="xs"
                              aria-label={`Field for ${v.label}`}
                            />
                            {v.component !== 'button' && (
                              <TextInput
                                placeholder="If empty, use"
                                value={m?.defaultValue ?? ''}
                                onChange={(e) => set(v, { defaultValue: e.currentTarget.value || null })}
                                size="xs"
                                aria-label={`Default for ${v.label}`}
                              />
                            )}
                          </Group>
                          {v.component === 'button' && (
                            <Text size="xs" c="dimmed">
                              The part of the link after {template.buttons[v.buttonIndex ?? 0]?.url?.replace(/\{\{\s*1\s*\}\}$/, '') ?? 'the fixed address'} is sent.
                            </Text>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              {unmapped.length > 0 && (
                <Text size="xs" c="orange">
                  Still to map: {unmapped.map((v) => v.label).join(', ')}.
                </Text>
              )}
              {error && <Alert color="red">{error}</Alert>}
            </Stack>

            <Stack gap="xs" data-help-id="communications.whatsapp-templates.link-preview">
              <Text size="sm" fw={600}>
                Preview with sample values
              </Text>
              {preview ? (
                <WhatsAppPhonePreview parts={preview} compact />
              ) : (
                <Text size="sm" c="dimmed">
                  Choose a template to see how the message will look.
                </Text>
              )}
              {template && (
                <Group gap={6} justify="center">
                  <Badge variant="light" color={statusColor(template.status.code, template.status.color)} size="sm">
                    {template.status.name}
                  </Badge>
                  <Badge variant="light" color="gray" size="sm">
                    {template.languageCode}
                  </Badge>
                  {variablesOf(template.bodyText).length > 0 && (
                    <Badge variant="light" color="gray" size="sm">
                      {template.variableCount} variable{template.variableCount === 1 ? '' : 's'}
                    </Badge>
                  )}
                </Group>
              )}
            </Stack>
          </SimpleGrid>
        )}

        <Group justify="space-between">
          <div>
            {current?.link && (
              <Button variant="subtle" color="red" leftSection={<IconLinkOff size={16} />} onClick={onUnlink} loading={unlink.isPending}>
                Unlink
              </Button>
            )}
          </div>
          <Group gap="xs">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              leftSection={<IconLink size={16} />}
              onClick={onSave}
              loading={save.isPending}
              disabled={!current || !template || unmapped.length > 0 || !!notLinkable}
              data-help-id="communications.whatsapp-templates.link-save"
            >
              Save link
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
